<template>
  <div class="shop-page">
    <!-- 상단 프로모션 배너 -->
    <div class="promo-banner">
      <div class="promo-text">
        <span class="promo-icon">🧧</span>
        설 연휴 특별 할인! 전 상품 <strong>최대 50% OFF</strong> + 무료배송
        <span class="promo-icon">🧧</span>
      </div>
    </div>

    <!-- 히어로 배너 섹션 -->
    <section class="hero-banner">
      <div class="hero-content">
        <div class="hero-badge">
          <span>2025 설 특집</span>
        </div>
        <h1 class="hero-title">
          새해 복 많이 받으세요!
          <span class="title-deco">🎊</span>
        </h1>
        <p class="hero-subtitle">
          알뜰 주부님들을 위한 특별한 설 선물<br/>
          반찬통 세트부터 주방용품까지 최대 50% 할인
        </p>
        <div class="hero-countdown">
          <div class="countdown-label">특가 마감까지</div>
          <div class="countdown-timer">
            <div class="time-block">
              <span class="time-value">{{ countdown.days }}</span>
              <span class="time-label">일</span>
            </div>
            <span class="time-separator">:</span>
            <div class="time-block">
              <span class="time-value">{{ countdown.hours }}</span>
              <span class="time-label">시간</span>
            </div>
            <span class="time-separator">:</span>
            <div class="time-block">
              <span class="time-value">{{ countdown.minutes }}</span>
              <span class="time-label">분</span>
            </div>
            <span class="time-separator">:</span>
            <div class="time-block">
              <span class="time-value">{{ countdown.seconds }}</span>
              <span class="time-label">초</span>
            </div>
          </div>
        </div>
      </div>
      <div class="hero-decoration">
        <div class="lantern lantern-1">🏮</div>
        <div class="lantern lantern-2">🏮</div>
        <div class="lantern lantern-3">🏮</div>
        <div class="gold-coin coin-1">🪙</div>
        <div class="gold-coin coin-2">🪙</div>
        <div class="plum-blossom blossom-1">🌸</div>
        <div class="plum-blossom blossom-2">🌸</div>
      </div>
    </section>

    <!-- 카테고리 네비게이션 -->
    <section class="category-nav">
      <div class="category-wrapper">
        <div 
          v-for="category in categories" 
          :key="category.id"
          :class="['category-item', { active: selectedCategory === category.id }]"
          @click="selectedCategory = category.id"
        >
          <img :src="category.icon" :alt="category.name" class="category-icon-img" />
          <span class="category-name">{{ category.name }}</span>
        </div>
      </div>
    </section>

    <!-- 타임딜 섹션 -->
    <section class="time-deal-section">
      <div class="section-header">
        <div class="section-title-wrapper">
          <span class="section-emoji">⚡</span>
          <h2 class="section-title">오늘의 타임딜</h2>
          <v-chip color="error" size="small" variant="flat" class="ml-2">HOT</v-chip>
        </div>
        <p class="section-subtitle">지금 이 가격, 다시 없을 기회!</p>
      </div>
      
      <div class="time-deal-grid">
        <div v-for="product in timeDealProducts" :key="product.id" class="time-deal-card">
          <div class="deal-badge">{{ product.discount }}%</div>
          <div class="product-image">
            <img :src="product.image" :alt="product.name" class="product-img" />
          </div>
          <div class="product-info">
            <div class="product-name">{{ product.name }}</div>
            <div class="product-desc">{{ product.description }}</div>
            <div class="price-wrapper">
              <span class="original-price">{{ formatPrice(product.originalPrice) }}</span>
              <span class="sale-price">{{ formatPrice(product.salePrice) }}</span>
            </div>
            <div class="deal-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: product.soldPercent + '%' }"></div>
              </div>
              <span class="sold-text">{{ product.soldPercent }}% 판매</span>
            </div>
          </div>
          <v-btn color="error" variant="flat" block class="buy-btn" @click="addToCart(product)">
            <v-icon start size="small">mdi-cart</v-icon>
            바로구매
          </v-btn>
        </div>
      </div>
    </section>

    <!-- 베스트셀러 섹션 -->
    <section class="best-section">
      <div class="section-header">
        <div class="section-title-wrapper">
          <span class="section-emoji">👑</span>
          <h2 class="section-title">베스트셀러</h2>
        </div>
        <p class="section-subtitle">주부님들이 가장 많이 찾는 인기 상품</p>
      </div>
      
      <div class="product-grid">
        <div v-for="(product, index) in bestProducts" :key="product.id" class="product-card">
          <div class="rank-badge">{{ index + 1 }}</div>
          <div class="product-image-wrapper">
            <img :src="product.image" :alt="product.name" class="product-img-large" />
            <div class="product-tags">
              <span v-if="product.isNew" class="tag tag-new">NEW</span>
              <span v-if="product.isBest" class="tag tag-best">BEST</span>
              <span v-if="product.discount > 0" class="tag tag-sale">{{ product.discount }}%</span>
            </div>
            <button class="wishlist-btn" @click="toggleWishlist(product)">
              <v-icon :color="product.isWishlisted ? 'error' : 'grey'">
                {{ product.isWishlisted ? 'mdi-heart' : 'mdi-heart-outline' }}
              </v-icon>
            </button>
          </div>
          <div class="product-content">
            <div class="product-brand">{{ product.brand }}</div>
            <div class="product-name">{{ product.name }}</div>
            <div class="product-rating">
              <div class="stars">
                <v-icon v-for="n in 5" :key="n" size="14" :color="n <= product.rating ? 'amber' : 'grey-lighten-2'">
                  mdi-star
                </v-icon>
              </div>
              <span class="review-count">({{ product.reviews }})</span>
            </div>
            <div class="product-price">
              <span v-if="product.discount > 0" class="original-price">{{ formatPrice(product.originalPrice) }}</span>
              <span class="current-price">{{ formatPrice(product.salePrice) }}</span>
            </div>
            <div class="product-benefits">
              <span class="benefit-tag" v-if="product.freeShipping">무료배송</span>
              <span class="benefit-tag" v-if="product.giftAvailable">선물포장</span>
            </div>
          </div>
          <v-btn color="primary" variant="flat" block class="add-cart-btn" @click="addToCart(product)">
            <v-icon start size="small">mdi-cart-plus</v-icon>
            장바구니 담기
          </v-btn>
        </div>
      </div>
    </section>

    <!-- 설 선물세트 섹션 -->
    <section class="gift-section">
      <div class="gift-banner">
        <div class="gift-content">
          <div class="gift-badge">🎁 설 선물 세트</div>
          <h2 class="gift-title">소중한 분께 마음을 전하세요</h2>
          <p class="gift-subtitle">정성이 담긴 선물세트, 무료 포장 서비스 제공</p>
        </div>
      </div>
      
      <div class="gift-grid">
        <div v-for="gift in giftSets" :key="gift.id" class="gift-card">
          <div class="gift-image">
            <img :src="gift.image" :alt="gift.name" class="gift-img" />
          </div>
          <div class="gift-info">
            <div class="gift-name">{{ gift.name }}</div>
            <div class="gift-desc">{{ gift.description }}</div>
            <div class="gift-price">{{ formatPrice(gift.price) }}</div>
            <div class="gift-includes">
              <span v-for="item in gift.includes" :key="item" class="include-item">{{ item }}</span>
            </div>
          </div>
          <v-btn color="error" variant="outlined" block class="gift-btn" @click="addToCart(gift)">
            선물하기
          </v-btn>
        </div>
      </div>
    </section>

    <!-- 추천 상품 섹션 -->
    <section class="recommend-section">
      <div class="section-header">
        <div class="section-title-wrapper">
          <span class="section-emoji">💝</span>
          <h2 class="section-title">주부님들의 Pick</h2>
        </div>
        <p class="section-subtitle">실용적이고 알뜰한 생활용품 모음</p>
      </div>
      
      <div class="recommend-grid">
        <div v-for="product in recommendProducts" :key="product.id" class="recommend-card">
          <div class="recommend-image">
            <img :src="product.image" :alt="product.name" class="recommend-img" />
            <div class="discount-badge" v-if="product.discount > 0">-{{ product.discount }}%</div>
          </div>
          <div class="recommend-info">
            <div class="recommend-name">{{ product.name }}</div>
            <div class="recommend-price">
              <span class="sale">{{ formatPrice(product.salePrice) }}</span>
              <span class="original" v-if="product.discount > 0">{{ formatPrice(product.originalPrice) }}</span>
            </div>
            <div class="recommend-tags">
              <v-chip size="x-small" color="primary" variant="tonal" v-if="product.freeShipping">무료배송</v-chip>
              <v-chip size="x-small" color="success" variant="tonal" v-if="product.todayDelivery">오늘출발</v-chip>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 리뷰 섹션 -->
    <section class="review-section">
      <div class="section-header">
        <div class="section-title-wrapper">
          <span class="section-emoji">💬</span>
          <h2 class="section-title">고객 후기</h2>
        </div>
        <p class="section-subtitle">실제 구매 고객님들의 생생한 후기</p>
      </div>
      
      <div class="review-grid">
        <div v-for="review in reviews" :key="review.id" class="review-card">
          <div class="review-header">
            <img :src="review.avatar" :alt="review.name" class="reviewer-avatar-img" />
            <div class="reviewer-info">
              <div class="reviewer-name">{{ review.name }}</div>
              <div class="review-date">{{ review.date }}</div>
            </div>
            <div class="review-rating">
              <v-icon v-for="n in 5" :key="n" size="14" :color="n <= review.rating ? 'amber' : 'grey-lighten-2'">
                mdi-star
              </v-icon>
            </div>
          </div>
          <div class="review-product">{{ review.product }}</div>
          <div class="review-content">{{ review.content }}</div>
          <div class="review-helpful">
            <v-btn size="small" variant="text" color="grey">
              <v-icon start size="small">mdi-thumb-up-outline</v-icon>
              도움이 돼요 ({{ review.helpful }})
            </v-btn>
          </div>
        </div>
      </div>
    </section>

    <!-- 하단 배너 -->
    <section class="bottom-banner">
      <div class="banner-content">
        <div class="banner-text">
          <h3>설 연휴에도 배송됩니다!</h3>
          <p>1월 27일까지 주문 시 설 전 배송 보장</p>
        </div>
        <v-btn color="white" variant="flat" size="large" class="banner-btn">
          <v-icon start>mdi-truck-fast</v-icon>
          배송일정 확인
        </v-btn>
      </div>
    </section>

    <!-- 장바구니 플로팅 버튼 -->
    <div class="cart-float" @click="showCart = true">
      <v-badge :content="cartCount" color="error" :model-value="cartCount > 0">
        <v-btn icon color="primary" size="large" elevation="8">
          <v-icon>mdi-cart</v-icon>
        </v-btn>
      </v-badge>
    </div>

    <!-- 장바구니 다이얼로그 -->
    <v-dialog v-model="showCart" max-width="500">
      <v-card class="cart-dialog">
        <v-card-title class="cart-title">
          <v-icon class="mr-2">mdi-cart</v-icon>
          장바구니
          <v-spacer />
          <v-btn icon variant="text" @click="showCart = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-divider />
        
        <v-card-text class="cart-content" v-if="cart.length > 0">
          <div v-for="item in cart" :key="item.id" class="cart-item">
            <img :src="item.image" :alt="item.name" class="cart-item-img" />
            <div class="cart-item-info">
              <div class="cart-item-name">{{ item.name }}</div>
              <div class="cart-item-price">{{ formatPrice(item.salePrice) }}</div>
            </div>
            <div class="cart-item-quantity">
              <v-btn icon size="x-small" variant="outlined" @click="decreaseQuantity(item)">
                <v-icon size="small">mdi-minus</v-icon>
              </v-btn>
              <span class="quantity">{{ item.quantity }}</span>
              <v-btn icon size="x-small" variant="outlined" @click="increaseQuantity(item)">
                <v-icon size="small">mdi-plus</v-icon>
              </v-btn>
            </div>
            <v-btn icon size="small" color="grey" variant="text" @click="removeFromCart(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </v-card-text>
        
        <v-card-text v-else class="cart-empty">
          <v-icon size="64" color="grey-lighten-2">mdi-cart-outline</v-icon>
          <p>장바구니가 비어있습니다</p>
        </v-card-text>
        
        <v-divider />
        
        <v-card-actions class="cart-actions" v-if="cart.length > 0">
          <div class="cart-total">
            <span>총 결제금액</span>
            <span class="total-price">{{ formatPrice(cartTotal) }}</span>
          </div>
          <v-btn color="error" variant="flat" block size="large" class="checkout-btn">
            <v-icon start>mdi-credit-card</v-icon>
            결제하기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 스낵바 -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2000" location="top">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

// 타입 정의
interface Product {
  id: number;
  name: string;
  description?: string;
  image: string;
  brand?: string;
  originalPrice: number;
  salePrice: number;
  discount: number;
  rating?: number;
  reviews?: number;
  soldPercent?: number;
  isNew?: boolean;
  isBest?: boolean;
  isWishlisted?: boolean;
  freeShipping?: boolean;
  giftAvailable?: boolean;
  todayDelivery?: boolean;
  quantity?: number;
  includes?: string[];
  price?: number;
}

interface Review {
  id: number;
  name: string;
  avatar: string;
  date: string;
  rating: number;
  product: string;
  content: string;
  helpful: number;
}

interface Category {
  id: string;
  name: string;
  icon: string;
}

// 카운트다운 상태
const countdown = ref({
  days: '00',
  hours: '00',
  minutes: '00',
  seconds: '00'
});

let countdownInterval: number | null = null;

// 카운트다운 계산
const updateCountdown = () => {
  const targetDate = new Date('2025-01-28T00:00:00');
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  
  if (diff > 0) {
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    countdown.value = {
      days: String(days).padStart(2, '0'),
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0')
    };
  }
};

// 카테고리 (실제 이미지 사용)
const categories = ref<Category[]>([
  { id: 'all', name: '전체', icon: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=100&h=100&fit=crop' },
  { id: 'container', name: '반찬통', icon: 'https://images.unsplash.com/photo-1610725664285-7c57e6eeac3f?w=100&h=100&fit=crop' },
  { id: 'kitchen', name: '주방용품', icon: 'https://images.unsplash.com/photo-1556909114-44e3e70034e2?w=100&h=100&fit=crop' },
  { id: 'living', name: '생활용품', icon: 'https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=100&h=100&fit=crop' },
  { id: 'gift', name: '선물세트', icon: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=100&h=100&fit=crop' },
  { id: 'food', name: '식품', icon: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=100&h=100&fit=crop' }
]);

const selectedCategory = ref('all');

// 타임딜 상품 (실제 이미지 사용)
const timeDealProducts = ref<Product[]>([
  {
    id: 1,
    name: '프리미엄 스텐 반찬통 10종세트',
    description: '누수방지 밀폐력 UP!',
    image: 'https://images.unsplash.com/photo-1610725664285-7c57e6eeac3f?w=400&h=400&fit=crop',
    originalPrice: 89000,
    salePrice: 44500,
    discount: 50,
    soldPercent: 87
  },
  {
    id: 2,
    name: '인덕션 냄비세트 4종',
    description: '독일산 코팅, 5년보증',
    image: 'https://images.unsplash.com/photo-1584990347449-a5e5e800b678?w=400&h=400&fit=crop',
    originalPrice: 159000,
    salePrice: 79500,
    discount: 50,
    soldPercent: 72
  },
  {
    id: 3,
    name: '에어프라이어 12L 대용량',
    description: '360도 열풍순환',
    image: 'https://images.unsplash.com/photo-1626082927389-6cd097cdc6ec?w=400&h=400&fit=crop',
    originalPrice: 199000,
    salePrice: 119400,
    discount: 40,
    soldPercent: 95
  }
]);

// 베스트셀러 상품 (실제 이미지 사용)
const bestProducts = ref<Product[]>([
  {
    id: 4,
    name: '락앤락 반찬통 풀세트 32p',
    brand: '락앤락',
    image: 'https://images.unsplash.com/photo-1610725664285-7c57e6eeac3f?w=300&h=300&fit=crop',
    originalPrice: 128000,
    salePrice: 89600,
    discount: 30,
    rating: 5,
    reviews: 2847,
    isNew: false,
    isBest: true,
    isWishlisted: false,
    freeShipping: true,
    giftAvailable: true
  },
  {
    id: 5,
    name: '테팔 프라이팬 3종세트',
    brand: '테팔',
    image: 'https://images.unsplash.com/photo-1592154395949-9d07e4a44e09?w=300&h=300&fit=crop',
    originalPrice: 89000,
    salePrice: 62300,
    discount: 30,
    rating: 5,
    reviews: 1923,
    isNew: true,
    isBest: true,
    isWishlisted: false,
    freeShipping: true,
    giftAvailable: false
  },
  {
    id: 6,
    name: '스텐 믹싱볼 5종 + 채반세트',
    brand: '키친아트',
    image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?w=300&h=300&fit=crop',
    originalPrice: 45000,
    salePrice: 27000,
    discount: 40,
    rating: 4,
    reviews: 892,
    isNew: false,
    isBest: false,
    isWishlisted: true,
    freeShipping: true,
    giftAvailable: false
  },
  {
    id: 7,
    name: '실리콘 주방장갑 2세트',
    brand: '실리만',
    image: 'https://images.unsplash.com/photo-1591261730799-ee4e6c2d16d7?w=300&h=300&fit=crop',
    originalPrice: 28000,
    salePrice: 16800,
    discount: 40,
    rating: 5,
    reviews: 3421,
    isNew: false,
    isBest: true,
    isWishlisted: false,
    freeShipping: true,
    giftAvailable: false
  },
  {
    id: 8,
    name: '전자레인지 용기세트 20p',
    brand: '글라스락',
    image: 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=300&h=300&fit=crop',
    originalPrice: 79000,
    salePrice: 47400,
    discount: 40,
    rating: 4,
    reviews: 1256,
    isNew: true,
    isBest: false,
    isWishlisted: false,
    freeShipping: true,
    giftAvailable: true
  },
  {
    id: 9,
    name: '주방 정리선반 3단',
    brand: '리빙박스',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=300&h=300&fit=crop',
    originalPrice: 55000,
    salePrice: 38500,
    discount: 30,
    rating: 4,
    reviews: 678,
    isNew: false,
    isBest: false,
    isWishlisted: false,
    freeShipping: true,
    giftAvailable: false
  }
]);

// 선물세트 (실제 이미지 사용)
const giftSets = ref<Product[]>([
  {
    id: 10,
    name: '설날 효도선물 세트 A',
    description: '부모님께 드리는 건강한 선물',
    image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400&h=400&fit=crop',
    price: 89000,
    originalPrice: 89000,
    salePrice: 89000,
    discount: 0,
    includes: ['홍삼정 1박스', '꿀 선물세트', '한과 모음']
  },
  {
    id: 11,
    name: '새댁 주방선물 세트',
    description: '신혼집에 딱 맞는 구성',
    image: 'https://images.unsplash.com/photo-1556909114-44e3e70034e2?w=400&h=400&fit=crop',
    price: 129000,
    originalPrice: 129000,
    salePrice: 129000,
    discount: 0,
    includes: ['프라이팬 세트', '냄비 세트', '조리도구 세트']
  },
  {
    id: 12,
    name: '프리미엄 반찬통 선물세트',
    description: '실용적인 선물의 정석',
    image: 'https://images.unsplash.com/photo-1607532941433-304659e8198a?w=400&h=400&fit=crop',
    price: 79000,
    originalPrice: 79000,
    salePrice: 79000,
    discount: 0,
    includes: ['스텐 반찬통 20p', '밀폐용기 10p', '정리 파우치']
  }
]);

// 추천 상품 (실제 이미지 사용)
const recommendProducts = ref<Product[]>([
  { id: 13, name: '만능 채칼 세트', image: 'https://images.unsplash.com/photo-1593618998160-e34014e67546?w=200&h=200&fit=crop', originalPrice: 35000, salePrice: 24500, discount: 30, freeShipping: true, todayDelivery: true },
  { id: 14, name: '주방 타이머', image: 'https://images.unsplash.com/photo-1563861826100-9cb868fdbe1c?w=200&h=200&fit=crop', originalPrice: 15000, salePrice: 9900, discount: 34, freeShipping: true, todayDelivery: false },
  { id: 15, name: '실리콘 냄비받침 4p', image: 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=200&h=200&fit=crop', originalPrice: 18000, salePrice: 12600, discount: 30, freeShipping: true, todayDelivery: true },
  { id: 16, name: '스텐 국자/뒤집개 세트', image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?w=200&h=200&fit=crop', originalPrice: 28000, salePrice: 19600, discount: 30, freeShipping: true, todayDelivery: true },
  { id: 17, name: '냉장고 정리용기 6p', image: 'https://images.unsplash.com/photo-1610725664285-7c57e6eeac3f?w=200&h=200&fit=crop', originalPrice: 32000, salePrice: 22400, discount: 30, freeShipping: true, todayDelivery: false },
  { id: 18, name: '양념통 세트 8p', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=200&h=200&fit=crop', originalPrice: 25000, salePrice: 17500, discount: 30, freeShipping: true, todayDelivery: true },
  { id: 19, name: '행주/수세미 세트', image: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=200&h=200&fit=crop', originalPrice: 12000, salePrice: 8400, discount: 30, freeShipping: false, todayDelivery: true },
  { id: 20, name: '주방 저울', image: 'https://images.unsplash.com/photo-1586985289688-ca3cf47d3e6e?w=200&h=200&fit=crop', originalPrice: 22000, salePrice: 15400, discount: 30, freeShipping: true, todayDelivery: false }
]);

// 리뷰 (실제 프로필 이미지 사용)
const reviews = ref<Review[]>([
  {
    id: 1,
    name: '김*희',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
    date: '2025.01.18',
    rating: 5,
    product: '프리미엄 스텐 반찬통 10종세트',
    content: '밀폐력이 정말 좋아요! 김치 넣어도 냄새 안새고 냉장고 정리도 깔끔하게 됐어요. 설 선물로 친정엄마께도 하나 더 사드릴 예정이에요 ㅎㅎ',
    helpful: 128
  },
  {
    id: 2,
    name: '박*영',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face',
    date: '2025.01.17',
    rating: 5,
    product: '테팔 프라이팬 3종세트',
    content: '역시 테팔이네요. 코팅 진짜 좋고 손잡이 분리되는 게 수납할 때 너무 편해요. 가격도 저렴하게 잘 샀어요!',
    helpful: 89
  },
  {
    id: 3,
    name: '이*수',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    date: '2025.01.16',
    rating: 4,
    product: '설날 효도선물 세트 A',
    content: '부모님 선물로 샀는데 포장이 정말 고급스러워요. 받으시고 좋아하셨습니다. 다만 배송이 하루 더 걸렸네요.',
    helpful: 56
  }
]);

// 장바구니
const cart = ref<Product[]>([]);
const showCart = ref(false);

const cartCount = computed(() => cart.value.reduce((sum, item) => sum + (item.quantity || 1), 0));
const cartTotal = computed(() => cart.value.reduce((sum, item) => sum + item.salePrice * (item.quantity || 1), 0));

// 스낵바
const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
});

// 유틸 함수
const formatPrice = (price: number) => {
  return price.toLocaleString('ko-KR') + '원';
};

const addToCart = (product: Product) => {
  const existingItem = cart.value.find(item => item.id === product.id);
  if (existingItem) {
    existingItem.quantity = (existingItem.quantity || 1) + 1;
  } else {
    cart.value.push({ ...product, quantity: 1 });
  }
  snackbar.value = { show: true, text: '장바구니에 추가되었습니다!', color: 'success' };
};

const removeFromCart = (product: Product) => {
  const index = cart.value.findIndex(item => item.id === product.id);
  if (index > -1) {
    cart.value.splice(index, 1);
  }
};

const increaseQuantity = (product: Product) => {
  product.quantity = (product.quantity || 1) + 1;
};

const decreaseQuantity = (product: Product) => {
  if ((product.quantity || 1) > 1) {
    product.quantity = (product.quantity || 1) - 1;
  } else {
    removeFromCart(product);
  }
};

const toggleWishlist = (product: Product) => {
  product.isWishlisted = !product.isWishlisted;
  snackbar.value = {
    show: true,
    text: product.isWishlisted ? '위시리스트에 추가되었습니다!' : '위시리스트에서 제거되었습니다.',
    color: product.isWishlisted ? 'success' : 'info'
  };
};

onMounted(() => {
  updateCountdown();
  countdownInterval = window.setInterval(updateCountdown, 1000);
});

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});
</script>

<style lang="scss" scoped>
@import '../../assets/styles/adminShop.scss';
</style>
